import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, Modal, Form, message, Select, Empty, Input, Upload, Tooltip, Switch, Divider } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { getAllDevices, addDevice, updateDevice, deleteDevice, batchDeleteDevices, getAllRooms, downloadDeviceTemplate, batchImportDevices } from '../../api';
import ListToolbar from '../../components/ListToolbar';
import { exportToCSV } from '../../utils/exportUtils';
import PageTitle from '../../components/PageTitle';
import ResizableTable from '../../components/ResizableTable';
import { isDevice4GMeter } from '../../utils/deviceMeter';
import MeterSupplementModal from '../../components/MeterSupplementModal';
import MeterMeteringFactorModal from '../../components/MeterMeteringFactorModal';
import MeterMeteringTotalModal from '../../components/MeterMeteringTotalModal';
import { showBatchImportResult, showBatchImportHttpError } from '../../utils/batchImportModal';

const { Option } = Select;
// 设备状态配置（复用原有）
const DEVICE_STATUS_OPTIONS = [
  { value: '1', label: '在线/正常' },
  { value: '0', label: '离线/故障' },
];
// 设备类型选项
const DEVICE_TYPE_OPTIONS = [
  { value: 1, label: '电表' },
  { value: 2, label: '水表' },
  { value: 3, label: '门锁' },
];
// ✅ 固化类型：电表=1（用于列表筛选和显示）
const CURRENT_DEVICE_TYPE = { value: 1, label: '电表' };

const ElectricMeterList = () => {
  const [devices, setDevices] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingDevice, setEditingDevice] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [supplementDevice, setSupplementDevice] = useState(null);
  const [meteringFactorDevice, setMeteringFactorDevice] = useState(null);
  const [meteringTotalDevice, setMeteringTotalDevice] = useState(null);
  const [electricMeterNeedReadIds, setElectricMeterNeedReadIds] = useState(() => new Set());
  const watchedCommunicationType = Form.useWatch('communication_type', form);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // 加载房间列表
  const loadRooms = async () => {
    try {
      const response = await getAllRooms();
      const roomList = response?.success && Array.isArray(response.data) ? response.data : [];
      setRooms(roomList);
    } catch (error) {
      console.error('加载房间失败：', error);
      message.error('加载房间列表失败');
    }
  };

  // ✅ 加载数据时，自动过滤【电表】类型
  const loadDevices = async () => {
    try {
      setLoading(true);
      const response = await getAllDevices(CURRENT_DEVICE_TYPE.value);
      const deviceList = response?.success && Array.isArray(response.data)
        ? response.data.filter(item => item.device_type === CURRENT_DEVICE_TYPE.value)
        : [];
      setDevices(deviceList);
      setAllDevices(deviceList);
      message.success(`加载成功 ${deviceList.length} 个${CURRENT_DEVICE_TYPE.label}`);
    } catch (error) {
      console.error('加载设备失败：', error);
      const errorMsg = error.response?.data?.error || `加载${CURRENT_DEVICE_TYPE.label}列表失败，请稍后重试`;
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRooms();
    loadDevices();
  }, []);

  // 搜索逻辑（复用原有）
  useEffect(() => {
    let filteredDevices = [...allDevices];
    if (searchText.trim()) {
      const keyword = searchText.trim();
      filteredDevices = filteredDevices.filter(device =>
        device.device_mac.includes(keyword) ||
        device.factory_meter_no.includes(keyword) ||
        device.current_meter_no.includes(keyword) ||
        (device.imei && String(device.imei).includes(keyword)) ||
        (device.room_name && device.room_name.includes(keyword)) ||
        (device.house_name && device.house_name.includes(keyword))
      );
    }
    setDevices(filteredDevices);
  }, [searchText, allDevices]);

  useEffect(() => {
    setPagination((prev) => {
      const total = devices.length;
      const maxPage = Math.max(1, Math.ceil(total / prev.pageSize));
      return {
        ...prev,
        total,
        current: Math.min(prev.current, maxPage),
      };
    });
  }, [devices]);

  // 处理搜索
  const handleSearch = () => {
    // 实时搜索，不需要额外处理
  };

  // 重置搜索
  const handleReset = () => {
    setSearchText('');
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  // 刷新数据
  const handleRefresh = () => {
    loadDevices();
  };

  // 导出数据
  const handleExport = () => {
    const exportData = devices.map(device => ({
      '电表ID': device.id,
      '设备MAC': device.device_mac || '-',
      '厂商表号': device.factory_meter_no || '-',
      '当前表号': device.current_meter_no || '-',
      'IMEI': device.imei || '-',
      '所属厂家': (() => {
        const code = String(device.manufacturer || '').trim();
        if (code === 'ZT') return '厂家一';
        return code || '-';
      })(),
      '设备详细类型': isDevice4GMeter(device) ? (device.device_detail_type === 1 ? '4G主表' : '蓝牙副表') : '',
      '通讯类型': (() => {
        const t = Number(device?.communication_type);
        if (t === 0) return '4G';
        if (t === 1) return '蓝牙';
        if (t === 2) return 'WIFI';
        return '-';
      })(),
      '设备状态': device.device_status_name || '-',
      '电表余额(元)': Number(device.electric_balance || 0).toFixed(2),
      '剩余电量(kWh)': Number(device.electric_quantity || 0).toFixed(2),
      '最低充值(元)': Number(device.min_recharge || 0).toFixed(2),
      '管理费(元)': Number(device.management_fee || 0).toFixed(2),
      '总用电量(kWh)': Number(device.total_electricity || 0).toFixed(2),
      '电价(元/kWh)': Number(device.electric_price || 0).toFixed(2),
      '绑定房间': device.room_name || '-',
      '所属房源': device.house_name || '-',
      '创建时间': device.created_at || '-',
      '更新时间': device.updated_at || '-',
    }));

    if (exportToCSV(exportData, '电表列表')) {
      message.success(`成功导出 ${exportData.length} 条数据`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  // 下载批量导入模板
  const handleDownloadTemplate = async () => {
    try {
      await downloadDeviceTemplate();
      message.success('模板下载成功');
    } catch (error) {
      console.error('下载模板失败：', error);
      message.error('下载模板失败，请重试');
    }
  };

  // 批量导入设备
  const fileInputRef = useRef(null);
  const handleBatchImport = () => {
    // 触发文件选择
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 检查文件类型
    if (!file.name.endsWith('.csv')) {
      message.error('请选择CSV文件');
      return;
    }

    try {
      setLoading(true);
      const res = await batchImportDevices(file);
      showBatchImportResult(res, { entityName: '设备' });
      if (res?.success) {
        loadDevices();
      }
    } catch (error) {
      console.error('批量导入失败：', error);
      showBatchImportHttpError(error);
    } finally {
      setLoading(false);
      // 清空文件输入
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // ✅ 弹窗赋值：自动带入电表类型，隐藏类型选择
  const showModal = (device = null) => {
    setEditingDevice(device);
    if (device) {
      form.setFieldsValue({
        device_type: device.device_type != null ? Number(device.device_type) : undefined,
        device_mac: device.device_mac,
        factory_meter_no: device.factory_meter_no,
        current_meter_no: device.current_meter_no,
        communication_type: device.communication_type === 0 ? 0 : 1,
        // 仅 4G 设备才有“4G主表/蓝牙副表”详细类型；蓝牙设备这里保持为空
        device_detail_type: device.communication_type === 0 ? (device.device_detail_type === 1 ? 1 : 0) : undefined,
        device_status: device.device_status.toString(),
        electric_balance: device.electric_balance || 0,
        electric_quantity: device.electric_quantity || 0,
        min_recharge: device.min_recharge || 0,
        management_fee: device.management_fee ?? 0,
        electric_price: device.electric_price ?? 0,
        show_price: Number(device.show_price) === 1,
        show_management_fee: Number(device.show_management_fee) === 1,
        show_service_fee: Number(device.show_service_fee) === 1,
        bind_room_id: device.bind_room_id ? device.bind_room_id.toString() : '',
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  // ✅ 提交时：新增只传当前表号，编辑保持原有逻辑
  const handleSubmit = async () => {
    try {
      if (editingDevice) {
        // 编辑模式：使用原有逻辑，保留所有字段
        const values = await form.validateFields();
        const communicationType = values.communication_type !== undefined && values.communication_type !== '' && values.communication_type != null
          ? parseInt(values.communication_type, 10)
          : 1;
        const submitData = {
          ...values,
          device_status: parseInt(values.device_status),
          bind_room_id: values.bind_room_id ? parseInt(values.bind_room_id) : 0,
          electric_balance: parseFloat(values.electric_balance) || 0,
          electric_quantity: parseFloat(values.electric_quantity) || 0,
          min_recharge: parseFloat(values.min_recharge) || 0,
          management_fee: parseFloat(values.management_fee) || 0,
          electric_price: parseFloat(values.electric_price) || 0,
          show_price: values.show_price ? 1 : 0,
          show_management_fee: values.show_management_fee ? 1 : 0,
          show_service_fee: values.show_service_fee ? 1 : 0,
          communication_type: communicationType,
          // 仅 4G 设备需要详细类型；蓝牙设备置空
          device_detail_type: communicationType === 0
            ? (values.device_detail_type !== undefined && values.device_detail_type !== '' && values.device_detail_type != null ? parseInt(values.device_detail_type, 10) : 0)
            : null,
        };
        // device_type：只有用户真的选了才传（没选=不修改，避免误覆盖/置空）
        if (values.device_type !== undefined && values.device_type !== '' && values.device_type != null) {
          submitData.device_type = parseInt(values.device_type, 10);
        } else {
          delete submitData.device_type;
        }
        // 允许编辑设备MAC（选填），保留在 submitData 中传给后端
        if (submitData.device_mac !== undefined && submitData.device_mac !== null) {
          submitData.device_mac = String(submitData.device_mac).trim() || null;
        }
        setLoading(true);
        const res = await updateDevice(editingDevice.id, submitData);
        res?.success ? message.success(`编辑${CURRENT_DEVICE_TYPE.label}成功`)
          : message.error(`编辑${CURRENT_DEVICE_TYPE.label}失败：` + (res?.error || '后端异常'));
        setIsModalVisible(false);
        loadDevices();
      } else {
        // 新增模式：只传当前表号和设备类型，其他使用默认值
        const values = await form.validateFields();
        const submitData = {
          current_meter_no: values.current_meter_no,
          device_type: parseInt(values.device_type), // 用户选择的设备类型（电表/水表/门锁）
          device_status: 1, // 默认：在线/正常
          device_mac: values.device_mac ? String(values.device_mac).trim() || undefined : undefined, // 选填
          // 其他字段不传，后端会使用默认值（余额=0，未绑定房间等）
        };
        setLoading(true);
        const res = await addDevice(submitData);
        const deviceTypeName = DEVICE_TYPE_OPTIONS.find(opt => opt.value === submitData.device_type)?.label || '设备';
        res?.success ? message.success(`新增${deviceTypeName}成功`)
          : message.error(`新增${deviceTypeName}失败：` + (res?.error || '后端异常'));
        setIsModalVisible(false);
        loadDevices();
      }
    } catch (error) {
      console.error('提交表单失败：', error);
      message.error('提交失败，请检查输入内容');
    } finally {
      setLoading(false);
    }
  };

  // 单个删除（复用原有）
  const handleDelete = async (id) => {
    if (!window.confirm(`确定要删除该${CURRENT_DEVICE_TYPE.label}吗？删除后不可恢复！`)) return;
    try {
      setLoading(true);
      await deleteDevice(id);
      message.success(`删除${CURRENT_DEVICE_TYPE.label}成功`);
      loadDevices();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('删除失败：', error);
      message.error(`删除${CURRENT_DEVICE_TYPE.label}失败，请稍后重试`);
    } finally {
      setLoading(false);
    }
  };

  // 批量删除（复用原有）
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(`请至少选择一个${CURRENT_DEVICE_TYPE.label}进行删除`);
      return;
    }
    if (!window.confirm(`确定要删除选中的${selectedRowKeys.length}个${CURRENT_DEVICE_TYPE.label}吗？删除后不可恢复！`)) return;
    try {
      setLoading(true);
      await batchDeleteDevices(selectedRowKeys);
      message.success(`成功删除${selectedRowKeys.length}个${CURRENT_DEVICE_TYPE.label}`);
      loadDevices();
      setSelectedRowKeys([]);
    } catch (error) {
      console.error('批量删除失败：', error);
      message.error(`批量删除${CURRENT_DEVICE_TYPE.label}失败，请稍后重试`);
    } finally {
      setLoading(false);
    }
  };

  // 分页变化处理
  const handleTableChange = (newPagination) => {
    setPagination({
          current: newPagination.current,
          pageSize: newPagination.pageSize,
          total: devices.length,
        });
    console.log('分页变化：', newPagination);
  };

  const pagedDevices = devices.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const handleSupplementElectric = (record) => {
    setSupplementDevice(record);
  };

  const markElectricSupplementNeedsRead = (deviceId) => {
    setElectricMeterNeedReadIds((prev) => new Set(prev).add(deviceId));
  };
  const clearElectricSupplementNeedsRead = (deviceId) => {
    setElectricMeterNeedReadIds((prev) => {
      const next = new Set(prev);
      next.delete(deviceId);
      return next;
    });
  };

  // 原始表格列配置
  const originalColumns = [
    {
      title: '操作',
      key: 'action',
      width: 300,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => showModal(record)}>编辑</Button>
          {isDevice4GMeter(record) && (
            <Tooltip
              title={
                electricMeterNeedReadIds.has(record.id)
                  ? '已下发过补量，请先在弹窗内抄表后再补电'
                  : undefined
              }
            >
              <span>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto' }}
                  disabled={electricMeterNeedReadIds.has(record.id)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSupplementElectric(record);
                  }}
                >
                  补电量
                </Button>
              </span>
            </Tooltip>
          )}
          {isDevice4GMeter(record) && (
            <>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', color: '#ff4d4f' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setMeteringFactorDevice(record);
                }}
              >
                计量系数
              </Button>
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: 'auto', color: '#1890ff' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setMeteringTotalDevice(record);
                }}
              >
                计量总额
              </Button>
            </>
          )}
        </Space>
      ),
    },
    { title: '电表ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: '设备MAC', dataIndex: 'device_mac', key: 'device_mac', width: 140 },
    { title: '厂商表号', dataIndex: 'factory_meter_no', key: 'factory_meter_no', width: 130 },
    { title: '当前表号', dataIndex: 'current_meter_no', key: 'current_meter_no', width: 130 },
    {
      title: '通讯类型',
      dataIndex: 'communication_type',
      key: 'communication_type',
      width: 100,
      render: (_, record) => {
        const t = Number(record?.communication_type);
        if (t === 0) return '4G';
        if (t === 1) return '蓝牙';
        if (t === 2) return 'WIFI';
        return '-';
      },
    },
    { title: '在线状态', dataIndex: 'is_online_name', key: 'is_online_name', width: 100 },
    {
      title: '设备厂家',
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      width: 140,
      render: (v) => {
        const code = String(v || '').trim();
        if (code === 'ZT') return '厂家一';
        return code || '-';
      },
    },
    { title: 'IMEI', dataIndex: 'imei', key: 'imei', width: 160, render: (v) => (v ? String(v) : '-') },
    {
      title: '设备详细类型',
      dataIndex: 'device_detail_type',
      key: 'device_detail_type',
      width: 120,
      render: (v, record) => (isDevice4GMeter(record) ? (Number(v) === 1 ? '4G主表' : '蓝牙副表') : ''),
    },
    { title: '设备状态', dataIndex: 'device_status_name', key: 'device_status_name', width: 90 },
    { title: '电表余额(元)', dataIndex: 'electric_balance', key: 'electric_balance', width: 100, render: (v) => (v || 0).toFixed(2) },
    { title: '剩余电量(kWh)', dataIndex: 'electric_quantity', key: 'electric_quantity', width: 110, render: (v) => (v || 0).toFixed(2) },
    { title: '最低充值(元)', dataIndex: 'min_recharge', key: 'min_recharge', width: 100, render: (v) => (v || 0).toFixed(2) },
    { title: '管理费(元/度)', dataIndex: 'management_fee', key: 'management_fee', width: 110, render: (v) => (Number(v) || 0).toFixed(3) },
    { title: '总用电量(kWh)', dataIndex: 'total_electricity', key: 'total_electricity', width: 110, render: (v) => (v || 0).toFixed(2) },
    { title: '电价(元/kWh)', dataIndex: 'electric_price', key: 'electric_price', width: 110, render: (v) => (Number(v) || 0).toFixed(3) },
    { title: '绑定房间', dataIndex: 'room_name', key: 'room_name', width: 100 },
    { title: '所属房源', dataIndex: 'house_name', key: 'house_name', width: 150 },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 160 },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 160 },
    {
      title: '删除',
      key: 'delete_action',
      width: 88,
      render: (_, record) => (
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
          删除
        </Button>
      ),
    },
  ];

  let electricColumnStates = {};
  try {
    const raw = localStorage.getItem('electricMeterList_columnStates');
    if (raw) electricColumnStates = JSON.parse(raw);
  } catch (e) {
    console.error('Failed to parse column states:', e);
  }
  const electricResizableColumns = originalColumns
    .map((col) => (electricColumnStates[col.key] === 'hidden' ? null : col))
    .filter(Boolean);

  const rowSelection = { selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys) };

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title={`${CURRENT_DEVICE_TYPE.label}管理`} />

      {/* 统一的工具栏 */}
      <ListToolbar
        searchPlaceholder="搜索MAC、表号、房间或房源名称"
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        showBatchDelete={true}
        selectedCount={selectedRowKeys.length}
        onBatchDelete={handleBatchDelete}
        showAdd={true}
        onAdd={() => showModal()}
        actions={[
          {
            key: 'download-template',
            label: '下载模板',
            icon: <DownloadOutlined />,
            onClick: handleDownloadTemplate,
            type: 'default'
          },
          {
            key: 'batch-import',
            label: '批量导入',
            icon: <UploadOutlined />,
            onClick: handleBatchImport,
            type: 'default'
          }
        ]}
      />

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <ResizableTable
        columns={electricResizableColumns.length > 0 ? electricResizableColumns : originalColumns}
        dataSource={pagedDevices}
        loading={loading}
        rowKey="id"
        storageKey="electricMeterList"
        rowSelection={rowSelection}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{ x: 2000 }}
        onChange={handleTableChange}
        locale={{ emptyText: <Empty description={`暂无${CURRENT_DEVICE_TYPE.label}数据`} /> }}
      />
      <Modal
        title={editingDevice ? `编辑${CURRENT_DEVICE_TYPE.label}` : '新增设备'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        width={600}
        destroyOnClose
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          {editingDevice ? (
            // 编辑模式：显示所有字段
            <>
              <Form.Item
                name="device_type"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <Select placeholder="请选择设备类型" options={DEVICE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="device_mac"
                label="设备MAC"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="选填" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="factory_meter_no"
                label="厂商表号"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入厂商表号" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="current_meter_no"
                label="当前表号"
                rules={[{ required: true, message: '请输入当前表号' }, { max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入当前表号" maxLength={50} />
              </Form.Item>
              <Form.Item name="communication_type" label="通讯类型">
                <Select placeholder="不填默认蓝牙" allowClear options={[
                  { value: 0, label: '4G' },
                  { value: 1, label: '蓝牙' },
                ]} />
              </Form.Item>
              <Form.Item name="device_detail_type" label="设备详细类型">
                <Select
                  placeholder={Number(watchedCommunicationType) === 0 ? '不填默认蓝牙副表' : '蓝牙设备无详细类型'}
                  allowClear
                  disabled={Number(watchedCommunicationType) !== 0}
                  options={[
                    { value: 0, label: '蓝牙副表' },
                    { value: 1, label: '4G主表' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="device_status"
                label="设备状态"
                rules={[{ required: true, message: '请选择设备状态' }]}
              >
                <Select placeholder="请选择设备状态" options={DEVICE_STATUS_OPTIONS} />
              </Form.Item>
              <Form.Item name="bind_room_id" label="绑定房间">
                <Select
                  placeholder="先输入关键词再选房间（房源名/房号）"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) => {
                    const q = String(input ?? '').trim().toLowerCase();
                    if (!q) return false;
                    return String(option?.label ?? '')
                      .toLowerCase()
                      .includes(q);
                  }}
                >
                  {rooms.map((room) => {
                    const label =
                      (room.houseName ?? room.house_name ?? '-') +
                      ' - ' +
                      (room.number ?? room.fang_hao ?? '-');
                    return (
                      <Option key={room.id} value={room.id.toString()} label={label}>
                        {label}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="electric_balance"
                  label="电表余额（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
                <Form.Item
                  name="electric_quantity"
                  label="剩余电量（kWh）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效电量（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
              </Space.Compact>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="min_recharge"
                  label="最低充值（元）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,2})?$/, message: '请输入有效金额（最多两位小数）' }]}
                >
                  <Input placeholder="默认0.00" />
                </Form.Item>
                <Form.Item
                  name="management_fee"
                  label="管理费（元/度）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,3})?$/, message: '请输入有效金额（最多三位小数）' }]}
                >
                  <Input placeholder="如 0.050" />
                </Form.Item>
              </Space.Compact>
              <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Form.Item
                  name="electric_price"
                  label="电价（元/kWh）"
                  style={{ flex: 1 }}
                  rules={[{ pattern: /^\d+(\.\d{1,3})?$/, message: '请输入有效单价（最多三位小数）' }]}
                >
                  <Input placeholder="如 0.560" />
                </Form.Item>
                <Form.Item label="总用电量（kWh）" style={{ flex: 1 }}>
                  <Input disabled value={(editingDevice.total_electricity || 0).toFixed(2)} />
                </Form.Item>
              </Space.Compact>
              <Divider orientation="left" style={{ margin: '8px 0 16px', fontSize: 13, color: '#666' }}>
                租客端显示（与小程序 test4 一致，不写入操作记录）
              </Divider>
              <Form.Item
                name="show_price"
                label="显示单价"
                valuePropName="checked"
              >
                <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
              </Form.Item>
              <Form.Item
                name="show_management_fee"
                label="显示管理费"
                valuePropName="checked"
              >
                <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
              </Form.Item>
              <Form.Item
                name="show_service_fee"
                label="显示服务费"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="显示"
                  unCheckedChildren="隐藏"
                  onChange={(checked) => {
                    if (checked) {
                      form.setFieldsValue({ show_service_fee: false });
                      Modal.confirm({
                        title: '提示',
                        content: '是否允许租客查看电表服务费？开启后租客将可见。',
                        okText: '确定',
                        cancelText: '取消',
                        onOk: () => form.setFieldsValue({ show_service_fee: true }),
                      });
                      return;
                    }
                    form.setFieldsValue({ show_service_fee: false });
                  }}
                />
              </Form.Item>
            </>
          ) : (
            // 新增模式：设备类型、当前表号（必填），设备MAC（选填）
            <>
              <Form.Item
                name="device_type"
                label="设备类型"
                rules={[{ required: true, message: '请选择设备类型' }]}
              >
                <Select placeholder="请选择设备类型" options={DEVICE_TYPE_OPTIONS} />
              </Form.Item>
              <Form.Item
                name="current_meter_no"
                label="当前表号"
                rules={[{ required: true, message: '请输入当前表号' }, { max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="请输入当前表号（必填）" maxLength={50} />
              </Form.Item>
              <Form.Item
                name="device_mac"
                label="设备MAC"
                rules={[{ max: 50, message: '最多50个字符' }]}
              >
                <Input placeholder="选填" maxLength={50} />
              </Form.Item>
              <div style={{ color: '#999', fontSize: '12px', marginTop: '-16px', marginBottom: '16px' }}>
                其他字段将使用默认值：设备状态=在线/正常，余额=0，未绑定房间
              </div>
            </>
          )}
        </Form>
      </Modal>

      <MeterSupplementModal
        open={!!supplementDevice}
        device={
          supplementDevice
            ? devices.find((d) => d.id === supplementDevice.id) || supplementDevice
            : null
        }
        variant="electric"
        supplementBlockedUntilRead={
          supplementDevice ? electricMeterNeedReadIds.has(supplementDevice.id) : false
        }
        onSupplementIssued={markElectricSupplementNeedsRead}
        onMeterReadSuccess={clearElectricSupplementNeedsRead}
        onClose={() => setSupplementDevice(null)}
        onRefresh={loadDevices}
      />

      <MeterMeteringFactorModal
        open={!!meteringFactorDevice}
        device={
          meteringFactorDevice
            ? devices.find((d) => d.id === meteringFactorDevice.id) || meteringFactorDevice
            : null
        }
        variant="electric"
        onClose={() => setMeteringFactorDevice(null)}
      />

      <MeterMeteringTotalModal
        open={!!meteringTotalDevice}
        device={
          meteringTotalDevice
            ? devices.find((d) => d.id === meteringTotalDevice.id) || meteringTotalDevice
            : null
        }
        variant="electric"
        onClose={() => setMeteringTotalDevice(null)}
      />
    </div>
  );
};
export default ElectricMeterList;